<template>
	<Modal v-model:visible="visible" :title="$t('tariffs.applyTariffModal.header', { title: tariffRef.title })" :width="650" @ok="onApplyTariff" @cancel="onCancel">
		<TariffActionSelect v-model="tariffAction" />
		<div v-if="tariffAction === ACTION_CHANGE" class="change-tariff-alert">
			<WarningOutlined class="warning-icon" />
			{{ $t('tariffs.applyTariffModal.changeTariffAlert') }}
		</div>
		<TypographyParagraph class="description">
			{{ description }}
			<TypographyText strong>{{ tariffRef.title }}</TypographyText>
		</TypographyParagraph>
		<ApplyTariffToListTable
			v-if="roversStore.isLoading || rovers.length > 0"
			:rovers="rovers"
			:loading="roversStore.isLoading"
			:rowSelection="rowSelection"
		/>
		<div class="params-grid">
			<AddDaysSelect v-model="addDays" :action="tariffAction" :disabled="rovers.length === 0" />
			<span>{{ $t('tariffs.applyTariffModal.totalPriceLabel') }}</span>
			<TypographyText strong>{{ $t('tariffs.applyTariffModal.totalPriceValue', totalPrice) }}</TypographyText>
		</div>
		<template #footer>
			<Button key="back" @click="onCancel">{{ $t('tariffs.applyTariffModal.cancelButtonLabel') }}</Button>
			<Button
				key="submit"
				type="primary"
				:disabled="isSubmitting || selectedCount == 0"
				:loading="isSubmitting"
				@click="onApplyTariff"
			>
				{{ $t('tariffs.applyTariffModal.tariffActions.' + tariffAction, selectedCount) }}
			</Button>
		</template>
	</Modal>
</template>

<script>
import { ref, computed, toRef, watch } from 'vue';
import { Modal, Button, TypographyParagraph, TypographyText } from 'ant-design-vue';
import { WarningOutlined } from '@ant-design/icons-vue';

import { useI18n } from 'vue-i18n';

import Utils from '@/api/Utils';

import { useRoversStore } from '@/stores/rovers';
import { useApplyTariffToListTable } from '@/components/tariffs/useApplyTariffToListTable';
import { useTariffUpdateApi } from '@/components/tariffs/useTariffApplyToListApi';
import { useModelProxy } from '@/utils/useModelProxy';

import ApplyTariffToListTable from '@/components/tariffs/ApplyTariffToListTable';
import TariffActionSelect from '@/components/tariffs/TariffActionSelect';
import AddDaysSelect from '@/components/tariffs/AddDaysSelect';
import { ACTION_CHANGE } from '@/components/tariffs/Consts';


export default {
	components: {
		Modal, Button, TypographyParagraph, TypographyText,
		ApplyTariffToListTable, TariffActionSelect, AddDaysSelect,
		WarningOutlined,
	},
	emits: ['update:modelValue'],
	props: ['modelValue', 'tariff'],
	setup(props, { emit }) {
		const { t } = useI18n();
		const roversStore = useRoversStore();
		const {
			rowSelection,
			selectedRowKeys,
			selectedCount,
			resetSelection,
		} = useApplyTariffToListTable(true);

		const visible = useModelProxy(props, emit);
		watch(visible, value => {
			if (value) {
				resetSelection();
				roversStore.updateRoversList();
			}
		});

		const tariffRef = toRef(props, 'tariff');
		const tariffAction = ref(ACTION_CHANGE);
		const rovers = computed(() => roversStore.rovers.filter(rover => {
			if (tariffAction.value === ACTION_CHANGE) {
				return rover.tariff == null || rover.tariff.base_tariff_id !== tariffRef.value.id;
			}
			return rover.tariff != null && rover.tariff.base_tariff_id === tariffRef.value.id;
		}));
		watch(tariffAction, resetSelection);
		const description = computed(() => {
			if (roversStore.isLoading || rovers.value.length > 0) {
				return t('tariffs.applyTariffModal.description.' + tariffAction.value);
			}
			return t('tariffs.applyTariffModal.descriptionNoRovers.' + tariffAction.value);
		});

		const addDays = ref(null);
		const totalPrice = computed(() => Utils.toRubles(selectedCount.value * (addDays.value ?? 0) * tariffRef.value.price_per_day));

		const selectedTariffId = computed(() => tariffRef.value.id);
		const { isSubmitting, onApplyTariff } = useTariffUpdateApi(selectedTariffId, addDays, selectedRowKeys, tariffAction);

		const onCancel = () => {
			visible.value = false;
		};
		return {
			visible,
			tariffRef,
			rovers,
			roversStore,
			isSubmitting,
			rowSelection,
			selectedCount,
			tariffAction,
			description,
			addDays,
			totalPrice,
			ACTION_CHANGE,

			onApplyTariff,
			onCancel,
		};
	}
}
</script>

<style scoped>
@import '@/assets/apply-tariff.css';

.params-grid {
	grid-template-columns: 140px 1fr;
}

.description {
	margin: 24px 0;
}
</style>